import { Box, IconButton, InputBase, Theme, Typography } from "@mui/material";
import { CONTROL_WIDTH, toNumber } from "@helpers/incomeProfileHelpers";

interface Props {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  testId: string;
}

const stepButtonSx = {
  width: 48,
  height: "100%",
  borderRadius: 0,
  bgcolor: "tokens.surface2",
  color: "primary.dark",
  "&:hover": { bgcolor: (theme: Theme) => `color-mix(in oklab, ${theme.palette.primary.main} 10%, transparent)` },
};

const StepGlyph = ({ children }: { children: string }) => (
  <Typography component="span" sx={{ fontFamily: "Outfit", fontSize: 16, fontWeight: 500, lineHeight: 1 }}>
    {children}
  </Typography>
);

/**
 * Replica `.mp-step` del perfil de ingresos: contador táctil de 44px con − y +, para no depender del
 * teclado numérico en móvil. También acepta teclear el número; siempre lo acota a `min`–`max`.
 */
export const InputStepper = ({ value, onChange, min, max, testId }: Props) => {
  const set = (next: number) => onChange(Math.max(min, Math.min(max, next)));

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: 44,
        width: { xs: "100%", sm: CONTROL_WIDTH },
        boxSizing: "border-box",
        border: "1px solid",
        borderColor: "tokens.borderStrong",
        borderRadius: "10px",
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <IconButton
        aria-label="Menos"
        onClick={() => set(value - 1)}
        disabled={value <= min}
        sx={[stepButtonSx, { borderRight: "1px solid", borderColor: "divider" }]}
      >
        <StepGlyph>−</StepGlyph>
      </IconButton>
      <InputBase
        value={value}
        onChange={(e) => set(toNumber(e.target.value.replace(/[^\d]/g, "")))}
        inputProps={{ inputMode: "numeric", "data-testid": testId }}
        sx={{
          flex: 1,
          minWidth: 0,
          "& input": {
            textAlign: "center",
            p: 0,
            fontFamily: "Outfit",
            fontSize: 15,
            fontWeight: 600,
            color: "primary.dark",
            fontVariantNumeric: "tabular-nums",
          },
        }}
      />
      <IconButton
        aria-label="Más"
        onClick={() => set(value + 1)}
        disabled={value >= max}
        sx={[stepButtonSx, { borderLeft: "1px solid", borderColor: "divider" }]}
      >
        <StepGlyph>+</StepGlyph>
      </IconButton>
    </Box>
  );
};
